import html2canvas from "html2canvas-pro";
import React from "react";
import { composeCanvasesToPdfBlob } from "../../utils/canvasPdfExport";
import type { CanvasPdfHotLink } from "../../utils/canvasPdfExport";
import { MediaKitPdfFooter } from "./MediaKitPdfFooter";

export const MEDIA_KIT_PDF_ROOT_ID = "mediakit-pdf-root";

/** Sections are captured separately so page breaks never split a section (unless the section is taller than one page). */
export const MEDIA_KIT_PDF_SECTION_SELECTOR = "[data-mediakit-pdf-section]";

/** Hidden from PDF snapshots (e.g. download control in footer). */
export const MEDIA_KIT_PDF_EXCLUDE_SELECTOR = "[data-mediakit-pdf-exclude]";

/** On-screen hidden appendix that must still be captured (e.g. YouTube video list). */
export const MEDIA_KIT_PDF_ONLY_SELECTOR = "[data-mediakit-pdf-only]";

/** Section uses content-sized width/height in the clone so html2canvas does not clip titles/footer text. */
const MEDIA_KIT_PDF_FLUID_SELECTOR = "[data-mediakit-pdf-fluid]";

/** Hero title: cap font size in PDF clone so windowWidth≥md does not apply 2.75rem and break wrapping. */
const MEDIA_KIT_PDF_CAP_HERO_TITLE_SELECTOR =
  "[data-mediakit-pdf-cap-hero-title]";

const S3_ORIGIN = "https://podscope.s3.amazonaws.com";
/** Total box width in px (border-box) for clone — matches ~md column. */
const CAPTURE_WIDTH_PX = 900;
/** Vertical padding on PDF root clone (matches ~MUI `p` block axis). */
const PDF_ROOT_PADDING_Y_PX = 24;
/** Extra horizontal inset in the PDF vs on-screen root so the export has more side breathing room. */
const PDF_ROOT_PADDING_X_PX = 40;
const SECTION_INNER_WIDTH_PX = CAPTURE_WIDTH_PX - PDF_ROOT_PADDING_X_PX * 2;
/** Locked height for full-page PDF sections (YouTube catalog table). Must stay under A4 content box. */
const FILL_PAGE_HEIGHT_PX = 1100;

async function fetchToDataUrl(src: string): Promise<string | null> {
  try {
    const res = await fetch(src, { mode: "cors", cache: "no-store" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function waitForElementImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              const done = () => resolve();
              const timer = window.setTimeout(done, 8000);
              img.addEventListener(
                "load",
                () => {
                  window.clearTimeout(timer);
                  done();
                },
                { once: true },
              );
              img.addEventListener(
                "error",
                () => {
                  window.clearTimeout(timer);
                  done();
                },
                { once: true },
              );
            }),
    ),
  );
}

async function buildS3CanvasMap(
  root: HTMLElement,
): Promise<Map<string, HTMLCanvasElement>> {
  const imgEls = Array.from(root.querySelectorAll("img")) as HTMLImageElement[];
  const srcToCanvas = new Map<string, HTMLCanvasElement>();

  await Promise.all(
    imgEls.map(async (img) => {
      if (!img.src || img.src.startsWith("data:")) return;
      const isHttp = img.src.startsWith("http://") || img.src.startsWith("https://");
      if (!isHttp) return;
      let dataUrl = await fetchToDataUrl(img.src);
      if (!dataUrl && img.src.startsWith(S3_ORIGIN)) {
        const altSrc = img.src.endsWith(".jpg")
          ? img.src.replace(/\.jpg$/, ".png")
          : img.src.endsWith(".png")
            ? img.src.replace(/\.png$/, ".jpg")
            : null;
        if (altSrc) dataUrl = await fetchToDataUrl(altSrc);
      }
      if (!dataUrl) return;

      await new Promise<void>((resolve) => {
        const paintImg = new Image();
        paintImg.onload = () => {
          const c = document.createElement("canvas");
          c.width = paintImg.naturalWidth || 52;
          c.height = paintImg.naturalHeight || 52;
          const ctx = c.getContext("2d");
          if (ctx) ctx.drawImage(paintImg, 0, 0);
          srcToCanvas.set(img.src, c);
          resolve();
        };
        paintImg.onerror = () => resolve();
        paintImg.src = dataUrl!;
      });
    }),
  );

  return srcToCanvas;
}

const PDF_CAPTURE_LIGHT_STYLE_ID = "mediakit-pdf-light-surfaces";

/** Light palette aligned with `createAppTheme("light")` — applied only to the html2canvas document clone. */
function injectLightModeForPdfCapture(doc: Document): void {
  if (!doc.head || doc.getElementById(PDF_CAPTURE_LIGHT_STYLE_ID)) return;

  const style = doc.createElement("style");
  style.id = PDF_CAPTURE_LIGHT_STYLE_ID;
  style.textContent = `
#${MEDIA_KIT_PDF_ROOT_ID},
#${MEDIA_KIT_PDF_ROOT_ID} .MuiPaper-root,
#${MEDIA_KIT_PDF_ROOT_ID} .MuiCard-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR},
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiPaper-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiCard-root {
  background-color: #ffffff !important;
  color: #1a1a1a !important;
  border-color: rgba(26, 25, 48, 0.1) !important;
  box-shadow: 0 1px 2px rgba(26, 25, 48, 0.08) !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiDivider-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiDivider-root {
  border-color: rgba(26, 25, 48, 0.1) !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiAvatar-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiAvatar-root {
  color: #1a1930 !important;
  background-color: #f8f8f8 !important;
  border-color: rgba(26, 25, 48, 0.12) !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-dark-heading],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-dark-heading] {
  color: #1a1930 !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-dark-secondary],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-dark-secondary] {
  color: #666666 !important;
  background-color: rgba(26, 25, 48, 0.06) !important;
}

/* Default body/heading Typography: dark mode uses near-white inline colors — override broadly. */
#${MEDIA_KIT_PDF_ROOT_ID} .MuiTypography-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiTypography-root {
  color: #1a1a1a !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiTypography-colorTextSecondary,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiTypography-colorTextSecondary {
  color: #666666 !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiTypography-colorPrimary,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiTypography-colorPrimary {
  color: #1a1930 !important;
}

/* MUI SvgIcon uses fill: currentColor — set both so html2canvas paints dark glyphs on white. */
#${MEDIA_KIT_PDF_ROOT_ID} .MuiSvgIcon-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiSvgIcon-root {
  color: #000000 !important;
  fill: currentColor !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-time-period-badge],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-time-period-badge] {
  background-color: #ffffff !important;
  color: #000000 !important;
  border: 1px solid rgba(26, 25, 48, 0.1) !important;
  box-shadow: 0 1px 2px rgba(26, 25, 48, 0.08) !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-time-period-label],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-time-period-label] {
  color: #000000 !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiButton-root .MuiSvgIcon-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiButton-root .MuiSvgIcon-root {
  color: currentColor !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} .MuiLink-root,
${MEDIA_KIT_PDF_SECTION_SELECTOR} .MuiLink-root {
  color: #1a1930 !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} svg text,
#${MEDIA_KIT_PDF_ROOT_ID} svg tspan,
${MEDIA_KIT_PDF_SECTION_SELECTOR} svg text,
${MEDIA_KIT_PDF_SECTION_SELECTOR} svg tspan {
  fill: #1a1a1a !important;
}

/* Audiencia (demography) bar rows — tight row stack; extra space under % before the bar (PDF clone). */
#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-audiencia-row],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-audiencia-row] {
  margin-bottom: 6px !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-audiencia-row] [data-mediakit-pdf-audiencia-label],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-audiencia-row] [data-mediakit-pdf-audiencia-label] {
  margin-bottom: 4px !important;
  margin-top: 0 !important;
}

#${MEDIA_KIT_PDF_ROOT_ID} [data-mediakit-pdf-audiencia-pct],
${MEDIA_KIT_PDF_SECTION_SELECTOR} [data-mediakit-pdf-audiencia-pct] {
  margin-bottom: 10px !important;
  margin-top: 0 !important;
}

${MEDIA_KIT_PDF_SECTION_SELECTOR} table th,
${MEDIA_KIT_PDF_SECTION_SELECTOR} table td {
  color: #1a1a1a !important;
}

${MEDIA_KIT_PDF_SECTION_SELECTOR} table th {
  background-color: #f4f4f6 !important;
}

${MEDIA_KIT_PDF_SECTION_SELECTOR} a[data-mediakit-pdf-link] {
  color: #1557c0 !important;
  text-decoration: underline !important;
}
`.trim();

  doc.head.appendChild(style);
}

const TIKTOK_BRAND_FILLS = new Set(["#25f4ee", "#fe2c55"]);

function expandHexForLuminance(hex: string): string {
  const raw = hex.trim().replace(/^#/, "").toLowerCase();
  if (raw.length === 3) {
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  }
  return hex.startsWith("#") ? hex : `#${hex}`;
}

/** sRGB relative luminance 0..1 */
function relativeLuminanceFromHex6(hex: string): number | null {
  const h = expandHexForLuminance(hex);
  const m = /^#([\da-f]{6})$/i.exec(h);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const lin = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function relativeLuminanceFromRgbString(rgb: string): number | null {
  const m = rgb.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
  if (!m) return null;
  const r = parseFloat(m[1]) / 255;
  const g = parseFloat(m[2]) / 255;
  const b = parseFloat(m[3]) / 255;
  const lin = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function isProbablyTikTokBrandIcon(svg: SVGSVGElement): boolean {
  return Array.from(svg.querySelectorAll("path[fill]")).some((p) => {
    const f = p.getAttribute("fill");
    if (!f?.startsWith("#")) return false;
    return TIKTOK_BRAND_FILLS.has(expandHexForLuminance(f).toLowerCase());
  });
}

function forceSvgShapeFillBlack(shape: SVGElement): void {
  shape.setAttribute("fill", "#000000");
  shape.style.setProperty("fill", "#000000", "important");
}

function forceSvgShapeStrokeBlack(shape: SVGElement): void {
  shape.setAttribute("stroke", "#000000");
  shape.style.setProperty("stroke", "#000000", "important");
}

/**
 * Theme often leaves `color` / inline `fill` as light grey on the clone; html2canvas then draws
 * low-contrast glyphs on white cards. Force near-black paints; skip TikTok multi-color paths.
 */
function applyLightSvgIconsForPdfClone(root: HTMLElement): void {
  root.querySelectorAll<SVGSVGElement>("svg.MuiSvgIcon-root").forEach((svg) => {
    if (svg.closest(".MuiButton-root")) return;

    if (isProbablyTikTokBrandIcon(svg)) {
      return;
    }

    svg.style.setProperty("color", "#000000", "important");
    svg.style.setProperty("fill", "#000000", "important");
    svg.style.setProperty("opacity", "1", "important");

    svg
      .querySelectorAll("path, circle, rect, ellipse, polygon, polyline, line")
      .forEach((node) => {
        const shape = node as SVGElement;
        const fill = shape.getAttribute("fill");
        const stroke = shape.getAttribute("stroke");

        if (fill === "none" || fill === "transparent") {
          if (
            !stroke ||
            stroke === "currentColor" ||
            stroke === "inherit" ||
            stroke === "none"
          ) {
            forceSvgShapeStrokeBlack(shape);
          }
          return;
        }

        if (fill === "white") {
          forceSvgShapeFillBlack(shape);
          return;
        }

        if (fill && fill.startsWith("#")) {
          const lower = expandHexForLuminance(fill).toLowerCase();
          if (TIKTOK_BRAND_FILLS.has(lower)) {
            return;
          }
          const lum = relativeLuminanceFromHex6(lower);
          if (lum != null && lum <= 0.2) {
            return;
          }
          forceSvgShapeFillBlack(shape);
          return;
        }

        if (fill && fill.startsWith("rgb")) {
          const lum = relativeLuminanceFromRgbString(fill);
          if (lum != null && lum <= 0.2) {
            return;
          }
          forceSvgShapeFillBlack(shape);
          return;
        }

        if (
          fill == null ||
          fill === "" ||
          fill === "currentColor" ||
          fill === "inherit"
        ) {
          forceSvgShapeFillBlack(shape);
        }
      });
  });
}

/**
 * Dark mode leaves `text.primary` / `primary.main` as near-white on many nodes (often as inline styles).
 * Setting clone inline `!important` guarantees html2canvas rasterizes readable text on white surfaces.
 */
function applyLightTextColorsForPdfClone(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(".MuiTypography-root").forEach((el) => {
    if (el.hasAttribute("data-mediakit-pdf-dark-heading")) {
      el.style.setProperty("color", "#1a1930", "important");
      return;
    }
    if (el.hasAttribute("data-mediakit-pdf-dark-secondary")) {
      el.style.setProperty("color", "#666666", "important");
      return;
    }
    if (el.classList.contains("MuiTypography-colorTextSecondary")) {
      el.style.setProperty("color", "#666666", "important");
      return;
    }
    if (el.classList.contains("MuiTypography-colorPrimary")) {
      el.style.setProperty("color", "#1a1930", "important");
      return;
    }
    if (el.classList.contains("MuiTypography-colorError")) {
      return;
    }
    el.style.setProperty("color", "#1a1a1a", "important");
  });

  root
    .querySelectorAll<HTMLElement>("[data-mediakit-pdf-time-period-badge]")
    .forEach((el) => {
      el.style.setProperty("background-color", "#ffffff", "important");
      el.style.setProperty("color", "#000000", "important");
      el.style.setProperty(
        "border",
        "1px solid rgba(26, 25, 48, 0.1)",
        "important",
      );
    });

  root
    .querySelectorAll<HTMLElement>("[data-mediakit-pdf-time-period-label]")
    .forEach((el) => {
      el.style.setProperty("color", "#000000", "important");
    });

  root.querySelectorAll<HTMLElement>(".MuiLink-root").forEach((el) => {
    if (el.closest(".MuiButton-root")) return;
    el.style.setProperty("color", "#1a1930", "important");
  });

  root.querySelectorAll<HTMLElement>(".MuiChip-label").forEach((el) => {
    if (el.closest(".MuiButton-root")) return;
    el.style.setProperty("color", "#1a1a1a", "important");
  });

  root.querySelectorAll<HTMLElement>(".MuiTableCell-root").forEach((el) => {
    el.style.setProperty("color", "#1a1a1a", "important");
  });

  root
    .querySelectorAll<HTMLElement>(".MuiListItemText-primary")
    .forEach((el) => {
      el.style.setProperty("color", "#1a1a1a", "important");
    });

  root
    .querySelectorAll<HTMLElement>(".MuiListItemText-secondary")
    .forEach((el) => {
      el.style.setProperty("color", "#666666", "important");
    });

  root
    .querySelectorAll<SVGTextContentElement>("svg text, svg tspan")
    .forEach((el) => {
      el.setAttribute("fill", "#1a1a1a");
      el.style.setProperty("fill", "#1a1a1a", "important");
    });

  applyLightSvgIconsForPdfClone(root);
}

function applyCloneCaptureFixes(
  cloned: HTMLElement,
  srcToCanvas: Map<string, HTMLCanvasElement>,
  options: { sectionWidthPx: number; isRoot?: boolean },
): void {
  Array.from(
    cloned.querySelectorAll<HTMLElement>(MEDIA_KIT_PDF_EXCLUDE_SELECTOR),
  ).forEach((el) => {
    el.style.display = "none";
  });

  // Match sm-tier hero title (2.25rem) even when virtual viewport is md+ for 3-col grids.
  Array.from(
    cloned.querySelectorAll<HTMLElement>(MEDIA_KIT_PDF_CAP_HERO_TITLE_SELECTOR),
  ).forEach((el) => {
    el.style.fontSize = "2.25rem";
    el.style.lineHeight = "1.2";
  });

  if (options.isRoot) {
    cloned.style.width = `${CAPTURE_WIDTH_PX}px`;
    cloned.style.maxWidth = `${CAPTURE_WIDTH_PX}px`;
    cloned.style.boxSizing = "border-box";
    cloned.style.padding = `${PDF_ROOT_PADDING_Y_PX}px ${PDF_ROOT_PADDING_X_PX}px`;
    cloned.style.backgroundColor = "#ffffff";
    cloned.style.overflow = "visible";
  } else if (cloned.matches(MEDIA_KIT_PDF_FLUID_SELECTOR)) {
    cloned.style.width = "auto";
    cloned.style.maxWidth = "none";
    cloned.style.minWidth = `${options.sectionWidthPx}px`;
    cloned.style.boxSizing = "border-box";
    cloned.style.backgroundColor = "#ffffff";
    cloned.style.overflow = "visible";
  } else {
    cloned.style.width = `${options.sectionWidthPx}px`;
    cloned.style.maxWidth = `${options.sectionWidthPx}px`;
    cloned.style.boxSizing = "border-box";
    cloned.style.backgroundColor = "#ffffff";
    cloned.style.overflow = "visible";
  }

  cloned.style.opacity = "1";

  if (cloned.hasAttribute("data-mediakit-pdf-fill-page")) {
    cloned.style.width = `${options.sectionWidthPx}px`;
    cloned.style.minWidth = `${options.sectionWidthPx}px`;
    cloned.style.maxWidth = `${options.sectionWidthPx}px`;
    cloned.style.height = `${FILL_PAGE_HEIGHT_PX}px`;
    cloned.style.minHeight = `${FILL_PAGE_HEIGHT_PX}px`;
    cloned.style.maxHeight = `${FILL_PAGE_HEIGHT_PX}px`;
    cloned.style.boxSizing = "border-box";
    cloned.style.overflow = "hidden";
  }

  if (cloned.matches(MEDIA_KIT_PDF_FLUID_SELECTOR)) {
    Array.from(cloned.querySelectorAll<HTMLElement>("*")).forEach((el) => {
      // Do not remove overflow:hidden from Avatar roots or any element using
      // border-radius for circular clipping — that would turn circles into squares.
      // Also preserve overflow:hidden on explicitly marked containers (e.g. company logo box).
      const isAvatarRoot =
        el.classList.contains("MuiAvatar-root") ||
        Array.from(el.classList).some((c) => c.startsWith("MuiAvatar-"));
      const keepOverflow =
        isAvatarRoot || el.hasAttribute("data-mediakit-pdf-keep-overflow");
      if (!keepOverflow) {
        el.style.overflow = "visible";
      }
      el.style.textOverflow = "clip";
      el.style.maxWidth = "none";
    });
  }

  Array.from(cloned.querySelectorAll<HTMLElement>("*")).forEach((el) => {
    if (el.style.opacity === "0") el.style.opacity = "1";
    if (el.style.visibility === "hidden") el.style.visibility = "visible";
  });

  // Platform section header: html2canvas keeps on-screen paddingTop (0.34em) which
  // already dropped the live title; stacking translateY made PDF titles sit low.
  Array.from(
    cloned.querySelectorAll<HTMLElement>("[data-mediakit-pdf-platform-header]"),
  ).forEach((header) => {
    header.style.alignItems = "center";
  });
  Array.from(
    cloned.querySelectorAll<HTMLElement>("[data-mediakit-pdf-platform-title]"),
  ).forEach((el) => {
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.transform = "none";
  });
  Array.from(
    cloned.querySelectorAll<HTMLElement>("[data-mediakit-pdf-platform-label]"),
  ).forEach((el) => {
    el.style.paddingTop = "0";
    el.style.paddingBottom = "0";
    el.style.lineHeight = "1";
    el.style.display = "flex";
    el.style.alignItems = "center";
    // html2canvas paints DM Serif slightly high vs SVG icons.
    el.style.transform = "translateY(0.08em)";
  });
  Array.from(
    cloned.querySelectorAll<HTMLElement>(
      "[data-mediakit-pdf-platform-icon-nudge]",
    ),
  ).forEach((cell) => {
    cell.style.display = "flex";
    cell.style.alignItems = "center";
    cell.style.justifyContent = "center";
    const mode = cell.getAttribute("data-mediakit-pdf-platform-icon-nudge");
    const svg = cell.querySelector("svg");
    if (!(svg instanceof SVGSVGElement)) return;
    const node = svg as unknown as HTMLElement;
    if (mode === "tiktok") {
      node.style.transform = "scale(0.82)";
      node.style.transformOrigin = "center center";
    } else {
      node.style.transform = "none";
    }
  });

  // Time period pill: symmetric flex box; html2canvas sits cap-height a hair high.
  Array.from(
    cloned.querySelectorAll<HTMLElement>(
      "[data-mediakit-pdf-time-period-badge]",
    ),
  ).forEach((el) => {
    el.style.display = "inline-flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.lineHeight = "1";
    el.style.boxSizing = "border-box";
    el.style.paddingTop = "0.45rem";
    el.style.paddingBottom = "0.45rem";
    el.style.paddingLeft = "20px";
    el.style.paddingRight = "20px";
  });
  Array.from(
    cloned.querySelectorAll<HTMLElement>(
      "[data-mediakit-pdf-time-period-label]",
    ),
  ).forEach((el) => {
    el.style.display = "block";
    el.style.lineHeight = "1";
    el.style.transform = "translateY(0.06em)";
  });

  Array.from(cloned.querySelectorAll("img")).forEach((img) => {
    const preCanvas = srcToCanvas.get(img.src);
    if (!preCanvas || !img.parentElement) return;
    const avatarRoot = img.closest<HTMLElement>(
      ".MuiAvatar-root,[class*='MuiAvatar-root']",
    );
    const c = document.createElement("canvas");
    if (avatarRoot) {
      // Draw a circular clip so the avatar pixel buffer matches what the browser shows.
      const size =
        avatarRoot.offsetWidth ||
        img.offsetWidth ||
        Math.min(preCanvas.width, preCanvas.height);
      c.width = size;
      c.height = size;
      const ctx = c.getContext("2d");
      if (ctx) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        // object-fit: cover — scale + centre
        const scale = Math.max(size / preCanvas.width, size / preCanvas.height);
        const dw = preCanvas.width * scale;
        const dh = preCanvas.height * scale;
        ctx.drawImage(preCanvas, (size - dw) / 2, (size - dh) / 2, dw, dh);
        ctx.restore();
      }
      c.style.cssText =
        "width:100%;height:100%;display:block;border-radius:50%;overflow:hidden;";
    } else {
      // For images inside a fixed-size keep-overflow container (e.g. company logo),
      // draw with object-fit:contain logic at the container's pixel dimensions so the
      // canvas never bleeds outside the box.
      const keepOverflowContainer = img.closest<HTMLElement>(
        "[data-mediakit-pdf-keep-overflow]",
      );
      if (keepOverflowContainer) {
        const w = keepOverflowContainer.offsetWidth || 80;
        const h = keepOverflowContainer.offsetHeight || 80;
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d");
        if (ctx) {
          const scale = Math.min(w / preCanvas.width, h / preCanvas.height);
          const dw = preCanvas.width * scale;
          const dh = preCanvas.height * scale;
          ctx.drawImage(preCanvas, (w - dw) / 2, (h - dh) / 2, dw, dh);
        }
        c.style.cssText = "display:block;width:100%;height:100%;";
      } else {
        const thumbCover = img.closest<HTMLElement>(
          "[data-mediakit-pdf-thumb-cover]",
        );
        if (thumbCover) {
          const w = thumbCover.offsetWidth || 120;
          const h = thumbCover.offsetHeight || 68;
          c.width = w;
          c.height = h;
          const ctx = c.getContext("2d");
          if (ctx) {
            const scale = Math.max(w / preCanvas.width, h / preCanvas.height);
            const dw = preCanvas.width * scale;
            const dh = preCanvas.height * scale;
            ctx.drawImage(preCanvas, (w - dw) / 2, (h - dh) / 2, dw, dh);
          }
          c.style.cssText = "display:block;width:100%;height:100%;";
        } else {
          c.width = preCanvas.width;
          c.height = preCanvas.height;
          const ctx = c.getContext("2d");
          if (ctx) ctx.drawImage(preCanvas, 0, 0);
          c.style.cssText =
            "width:100%;height:100%;display:block;object-fit:contain;vertical-align:top;";
        }
      }
    }
    img.parentElement.replaceChild(c, img);
  });

  if (cloned.matches(MEDIA_KIT_PDF_FLUID_SELECTOR)) {
    const w = Math.ceil(cloned.scrollWidth);
    cloned.style.width = `${Math.max(options.sectionWidthPx, w)}px`;
    // Add bottom padding instead of a hard minHeight: avoids locking a potentially
    // under-reported scrollHeight while still giving descenders & line-height room.
    cloned.style.height = "auto";
    cloned.style.paddingBottom = "20px";
  }

  applyLightTextColorsForPdfClone(cloned);
}

function collectSectionHotLinks(section: HTMLElement): CanvasPdfHotLink[] {
  const sectionRect = section.getBoundingClientRect();
  if (sectionRect.width <= 0 || sectionRect.height <= 0) return [];
  return Array.from(
    section.querySelectorAll<HTMLAnchorElement>("a[data-mediakit-pdf-link]"),
  )
    .map((anchor) => {
      const href = anchor.href;
      const rect = anchor.getBoundingClientRect();
      if (!href || rect.width <= 0 || rect.height <= 0) return null;
      return {
        href,
        xRatio: (rect.left - sectionRect.left) / sectionRect.width,
        yRatio: (rect.top - sectionRect.top) / sectionRect.height,
        wRatio: rect.width / sectionRect.width,
        hRatio: rect.height / sectionRect.height,
      };
    })
    .filter((link): link is CanvasPdfHotLink => link != null);
}

async function captureElementToCanvas(
  element: HTMLElement,
  srcToCanvas: Map<string, HTMLCanvasElement>,
  options: { sectionWidthPx: number; isRoot?: boolean },
): Promise<HTMLCanvasElement> {
  const fillPage = element.hasAttribute("data-mediakit-pdf-fill-page");
  return html2canvas(element, {
    scale: 2,
    useCORS: false,
    allowTaint: false,
    backgroundColor: "#ffffff",
    logging: false,
    // MUI Grid uses md (900px) for 3 columns (size md:4). Inner width is 852px but
    // windowWidth must be ≥900 so md applies; hero title is capped in applyCloneCaptureFixes.
    windowWidth: CAPTURE_WIDTH_PX,
    ...(fillPage
      ? {
          width: options.sectionWidthPx,
          height: FILL_PAGE_HEIGHT_PX,
          windowHeight: FILL_PAGE_HEIGHT_PX,
        }
      : {}),
    onclone: (clonedDoc: Document, cloned: HTMLElement) => {
      injectLightModeForPdfCapture(clonedDoc);
      applyCloneCaptureFixes(cloned, srcToCanvas, options);
    },
  });
}

/**
 * Renders the mediakit DOM to a multi-page A4 PDF with margins and section-aware page breaks.
 */
export async function captureMediaKitElementToPdfBlob(
  element: HTMLElement,
): Promise<Blob | null> {
  await waitForElementImages(element);
  const srcToCanvas = await buildS3CanvasMap(element);

  const sectionNodes = Array.from(
    element.querySelectorAll<HTMLElement>(MEDIA_KIT_PDF_SECTION_SELECTOR),
  );

  /** Matches MediaKitPdfFooter: bottom offset + divider + text row. */
  const footerReserveMm = 18;

  if (sectionNodes.length === 0) {
    const canvas = await captureElementToCanvas(element, srcToCanvas, {
      sectionWidthPx: CAPTURE_WIDTH_PX,
      isRoot: true,
    });
    return composeCanvasesToPdfBlob([canvas], {
      footer: React.createElement(MediaKitPdfFooter),
      footerReserveMm,
    });
  }

  const canvases: HTMLCanvasElement[] = [];
  const sectionLinks: Array<CanvasPdfHotLink[] | undefined> = [];
  for (const section of sectionNodes) {
    const links = collectSectionHotLinks(section);
    const c = await captureElementToCanvas(section, srcToCanvas, {
      sectionWidthPx: SECTION_INNER_WIDTH_PX,
      isRoot: false,
    });
    canvases.push(c);
    sectionLinks.push(links.length > 0 ? links : undefined);
  }

  return composeCanvasesToPdfBlob(canvases, {
    footer: React.createElement(MediaKitPdfFooter),
    footerReserveMm,
    sectionLinks,
  });
}
